import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, take } from 'rxjs';
import { ArticleItem } from '../models/article-item.model';
import { LicenseDependentButtonModel } from '../models/license-dependent-button.model';
import { LicenseResultModel } from '../models/license.result.model';
import { PaymentSummaryModel } from '../models/payment-summary.model';
import { LanguageService } from './language.service';

@Injectable({
  providedIn: 'root'
})
export class LicenseService {
  private licenseCache: { model: LicenseDependentButtonModel, subscription: Subject<LicenseResultModel> }[] = []

  constructor(private http: HttpClient, private languageService: LanguageService) { }

  checkLicense(data: LicenseDependentButtonModel, skipCache: boolean): Observable<LicenseResultModel> {
    let existingCacheItem = this.licenseCache.find(cacheItem => cacheItem.model.ActionType === data.ActionType &&
      cacheItem.model.ActionTypeRange === data.ActionTypeRange &&
      cacheItem.model.PaymentType === data.PaymentType &&
      cacheItem.model.IsPaymentDetailView === data.IsPaymentDetailView)

    if (existingCacheItem && !skipCache) {
      return existingCacheItem.subscription.asObservable();
    }

    if (!existingCacheItem) {
      existingCacheItem = {
        model: data,
        subscription: new Subject<LicenseResultModel>()
      }
      this.licenseCache.push(existingCacheItem);
    }

    let params = new HttpParams();
    if (data) {
      params = params.set('ActionType', data.ActionType);
    }
    if (data?.ActionTypeRange) {
      params = params.set('ActionTypeRange', data.ActionTypeRange);
    }
    if (data?.PaymentType) {
      params = params.set('PaymentType', data.PaymentType);
    }
    if (data?.IsPaymentDetailView) {
      params = params.set('IsPaymentDetailView', data.IsPaymentDetailView);
    }

    this.http.get<LicenseResultModel>(`${this.languageService.getLanguage()}/License/check`, {
      params
    }).pipe(
      take(1)
    ).subscribe((result) => {
      existingCacheItem?.subscription.next(result);
    });

    return existingCacheItem.subscription.asObservable();
  }

  getArticlesList(culture: string, transportMode: string): Observable<ArticleItem[]> {
    const params = new HttpParams().set('transportMode', transportMode);
    return this.http.get<ArticleItem[]>(`${culture}/SmartLabel/Generate/Articles`, {
      params
    });
  }

  selectArticle(culture: string, id: number) {
    const params = new HttpParams().set('id', id).set('isArticle', true);
    return this.http.get<ArticleItem[]>(`${culture}/SmartLabel/Generate/DangerousGoodSelected`, {
      params
    });
  }

  savePaymentSummary(paymentSummaryModel: PaymentSummaryModel): Observable<void> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { LicenseDependentButtonModel, ...newObj } = paymentSummaryModel;
    return this.http.post<void>(`${this.languageService.getLanguage()}/License/PaymentSummary`, {
      ...newObj,
      LicenseDependentButton: paymentSummaryModel.LicenseDependentButtonModel,
    });
  }

  checkIfIsAdmin(): Observable<void> {
    return this.http.get<void>(`${this.languageService.getLanguage()}/License/Admin`);
  }
}
